import {
    ActionIcon,
    Anchor,
    Group,
    Menu,
    rgba,
    Text,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconDotsVertical } from "@tabler/icons-react";
import { Link, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useFeature } from "flagged";
import { useState } from "react";
import { getShortcutText, useKeyboardShortcuts } from "src/hooks/useKeyboardShortcuts";
import { useSyncUserRoles } from "src/hooks/useSyncUserRoles";
import { useDatasourceOverviewWithAccess } from "src/hooks/useUser";
import { useAdmin, useNotAuthorizedUser } from "src/utils/useAdmin";
import { useEvaluator } from "src/utils/useEvaluator";
import { useExternalUser } from "src/utils/useExternalUser";
import { AdminMenu } from "./AdminMenu";
import { IngestionStatusOverviewModal } from "./IngestionStatusOverview";

interface HeaderNavProps {
    preferenceDisplay: string;
}

export function HeaderNav({ preferenceDisplay }: HeaderNavProps) {
    const theme = useMantineTheme();
    const router = useRouter();
    const isAdmin = useAdmin();
    const isEvaluator = useEvaluator();
    const isExternalUser = useExternalUser();
    const isTripAnalyzerEnabled = useFeature("TRIP_ANALYZER");
    const isAssetsViewerEnabled = useFeature("ASSETS_VIEWER");
    const { isForbidden } = useSyncUserRoles();
    const isNotAuthorized = useNotAuthorizedUser();
    const isMobile = useMediaQuery(`(max-width: 1250px)`);
    const [isPreferencesHovered, setIsPreferencesHovered] = useState(false);

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const isCurrentPage = (target: string) =>
        target === "/"
            ? pathname === "/" || pathname.startsWith("/chats")
            : pathname.startsWith(target);

    const activeTabStyle = (target: string) =>
        isCurrentPage(target)
            ? { fontWeight: 700, borderBottom: "2px solid currentColor", paddingBottom: 2 }
            : undefined;

    const { data: datasourceOverview } = useDatasourceOverviewWithAccess();
    const [modalOpened, { open, close }] = useDisclosure(false);

    useKeyboardShortcuts(
        [
            !isExternalUser && {
                key: "m",
                handler: open,
                description: "Knowledge Base Details",
            },
            {
                key: "i",
                handler: () => {
                    navigate({ to: "/" });
                    // Focus the chat input after navigation
                    setTimeout(() => {
                        (document.querySelector("#chat-input") as HTMLElement)?.focus();
                    }, 100);
                },
                description: "New Conversation",
            },
            !isExternalUser && {
                key: "b",
                handler: () => navigate({ to: "/questionnaire" }),
                description: "Preferences",
            },
        ].filter(Boolean) as { key: string; handler: () => void; description: string }[],
    );
    if (isForbidden || isNotAuthorized) return null;

    return (
        <Group
            ml="auto"
            style={{
                [`@media (maxWidth: ${theme.breakpoints.md})`]: {
                    gap: "4px",
                },
            }}
        >
            {/* Desktop links */}
            {!isMobile && (
                <Group gap="xs">
                    <Tooltip label={getShortcutText("I")}>
                        <Anchor component={Link} to="/" fz="14px" style={activeTabStyle("/")}>
                            Start a Conversation
                        </Anchor>
                    </Tooltip>
                    {!isExternalUser && (
                        <>
                            <Text c="gray.4" size="sm">
                                |
                            </Text>
                            <Anchor
                                component={Link}
                                to="/documents"
                                data-tour="documents-viewer"
                                fz="14px"
                                style={activeTabStyle("/documents")}
                            >
                                Documents Viewer
                            </Anchor>
                            {isAssetsViewerEnabled && (
                                <>
                                    <Text c="gray.4" size="sm">
                                        |
                                    </Text>
                                    <Anchor
                                        component={Link}
                                        to="/assets"
                                        data-tour="assets-viewer"
                                        fz="14px"
                                        style={activeTabStyle("/assets")}
                                    >
                                        Assets Viewer
                                    </Anchor>
                                </>
                            )}
                            <Text c="gray.4" size="sm">
                                |
                            </Text>
                            <Tooltip label={getShortcutText("M")}>
                                <Anchor
                                    fz="14px"
                                    id="data-source-details"
                                    onClick={open}
                                    aria-label="Knowledge Base Details"
                                >
                                    Knowledge Base Details
                                </Anchor>
                            </Tooltip>
                        </>
                    )}

                    {isEvaluator && (
                        <>
                            <Text c="gray.4" size="sm">
                                |
                            </Text>
                            <Anchor component={Link} to="/sme" fz="14px">
                                SME Feedback
                            </Anchor>
                        </>
                    )}
                    {isAdmin && (
                        <>
                            <Text c="gray.4" size="sm">
                                |
                            </Text>
                            <AdminMenu />
                        </>
                    )}
                </Group>
            )}
            {/* Mobile menu */}
            {isMobile && (
                <Menu withinPortal position="bottom-end" shadow="sm">
                    <Menu.Target>
                        <ActionIcon variant="light" aria-label="Open menu">
                            <IconDotsVertical size={18} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item component={Link} to="/" style={activeTabStyle("/")}>
                            Start a Conversation
                        </Menu.Item>
                        {!isExternalUser && (
                            <>
                                <Menu.Item
                                    component={Link}
                                    to="/documents"
                                    style={activeTabStyle("/documents")}
                                >
                                    Documents Viewer
                                </Menu.Item>
                                {isAssetsViewerEnabled && (
                                    <Menu.Item
                                        component={Link}
                                        to="/assets"
                                        style={activeTabStyle("/assets")}
                                    >
                                        Assets Viewer
                                    </Menu.Item>
                                )}
                                <Menu.Item>
                                    <Anchor
                                        style={{
                                            color: "black",
                                        }}
                                        fz="14px"
                                        id="data-source-details"
                                        onClick={open}
                                        aria-label="Knowledge Base Details"
                                    >
                                        Knowledge Base Details
                                    </Anchor>
                                </Menu.Item>
                            </>
                        )}
                        {isEvaluator && (
                            <Menu.Item component={Link} to="/sme">
                                SME Feedback
                            </Menu.Item>
                        )}
                        {isAdmin && (
                            <>
                                <Menu.Divider />
                                <Menu.Label>Admin</Menu.Label>
                                <Menu.Item component={Link} to="/feedback">
                                    Users Feedback
                                </Menu.Item>
                                {isTripAnalyzerEnabled && (
                                    <Menu.Item component={Link} to="/trip-analyzer">
                                        Trip Analyzer
                                    </Menu.Item>
                                )}
                                <Menu.Item component={Link} to="/manageUsers">
                                    Manage Users
                                </Menu.Item>
                                <Menu.Item component={Link} to="/manageRoles">
                                    Manage Roles
                                </Menu.Item>
                                <Menu.Item component={Link} to="/manageRequests">
                                    Manage Requests
                                </Menu.Item>
                                <Menu.Item component={Link} to="/manageTerms">
                                    Manage Terms
                                </Menu.Item>
                            </>
                        )}
                        {!isExternalUser && (
                            <>
                                <Menu.Divider />
                                <Menu.Item
                                    component={Link}
                                    to="/questionnaire"
                                    search={
                                        !router.state.location.pathname.includes("questionnaire")
                                            ? ({
                                                  source: router.state.location.pathname,
                                                  // biome-ignore lint/suspicious/noExplicitAny: Temporary to enforce TS typing
                                              } as any)
                                            : {}
                                    }
                                >
                                    Preferences
                                </Menu.Item>
                            </>
                        )}
                    </Menu.Dropdown>
                </Menu>
            )}
            {!isMobile && !isExternalUser && (
                <Tooltip label={getShortcutText("B")}>
                    <Anchor
                        component={Link}
                        to="/questionnaire"
                        search={
                            !router.state.location.pathname.includes("questionnaire")
                                ? ({
                                      source: router.state.location.pathname,
                                      // biome-ignore lint/suspicious/noExplicitAny: Temporary to enforce TS typing
                                  } as any)
                                : {}
                        }
                        onMouseEnter={() => setIsPreferencesHovered(true)}
                        onMouseLeave={() => setIsPreferencesHovered(false)}
                        style={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: theme.spacing.xs,
                            cursor: "pointer",
                            textDecoration: "none",
                            padding: "6px 12px",
                            borderRadius: theme.radius.md,
                            border: `1px solid ${theme.colors.gray[3]}`,
                            backgroundColor: isPreferencesHovered
                                ? rgba(theme.colors.violet[1], 0.5)
                                : rgba(theme.colors.violet[0], 0.3),
                            borderColor: isPreferencesHovered
                                ? theme.colors.violet[3]
                                : theme.colors.gray[3],
                            boxShadow: isPreferencesHovered
                                ? `0 2px 4px ${rgba(theme.colors.dark[9], 0.05)}`
                                : "none",
                            transition: "all 0.2s ease",
                        })}
                    >
                        <Text size="xs" fw={600} c={theme.colors.violet[7]}>
                            {preferenceDisplay}
                        </Text>
                        <IconDotsVertical size={12} stroke={1.5} color={theme.colors.violet[7]} />
                    </Anchor>
                </Tooltip>
            )}
            <IngestionStatusOverviewModal
                status={datasourceOverview}
                fullScreen={isMobile}
                opened={modalOpened}
                onClose={close}
            />
        </Group>
    );
}
